"use client";

import React, { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import DomFlag from "../../../public/dpmflag.png";

interface Domain {
  name: string;       // Country name (United Arab Emirates, South Korea, etc)
  maincat: string;    // Generic, European, IDN gTLD, etc
  extension: string;  // .com, xn--80asehdb, etc
  pricing: any;
}

interface ManagedServiceProps {
  countryname: string;
}

/* ---------------- HELPERS ---------------- */

const normalizeExt = (v: string) =>
  v.replace(/^\./, "").trim().toLowerCase();

const slugifyCountry = (name: string) =>
  name.toLowerCase().replace(/\s+/g, "_");

const getPriceAndTerm = (pricing: any) => {
  const reg = pricing?.domainregister ?? {};

  if (reg.msetupfee > 0) return { price: reg.msetupfee, term: "1 Year" };
  if (reg.qsetupfee > 0) return { price: reg.qsetupfee, term: "2 Years" };
  if (reg.ssetupfee > 0) return { price: reg.ssetupfee, term: "3 Years" };

  return { price: "N/A", term: "N/A" };
};

/* ---------------- COMPONENT ---------------- */

const DomainDataTable: React.FC<ManagedServiceProps> = ({ countryname }) => {
  const [domainData, setDomainData] = useState<Domain[]>([]);
  const [filteredData, setFilteredData] = useState<Domain[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  const countrynames = useMemo(
    () =>
      countryname === "all"
        ? [
            "Generic",
            "European",
            "Asia-Pacific",
            "American",
            "African",
            "Geographic",
            "Community",
          ]
        : [countryname],
    [countryname]
  );

  const payload = useMemo(
    () => ({
      currency: 1,
      category: countrynames.join(","),
      getprice: true,
    }),
    [countrynames]
  );

  useEffect(() => {
    const fetchData = async () => {
      const res = await fetch("/api/domains", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      setDomainData(data.domains || []);
      setFilteredData(data.domains || []);
    };

    fetchData();
  }, [payload]);

  /* ---------------- SEARCH ---------------- */

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    setSearchQuery(value);
    setCurrentPage(1);

    setFilteredData(
      domainData.filter(
        (d) =>
          d.name.toLowerCase().includes(value) ||
          d.maincat.toLowerCase().includes(value) ||
          d.extension.toLowerCase().includes(value)
      )
    );
  };

  /* ---------------- PAGINATION ---------------- */

  const paginated = filteredData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  const totalPages = Math.ceil(filteredData.length / entriesPerPage);

  return (
    <div className="custMargin">
      {/* Search + entries */}
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="d-flex align-items-center">
            Show
            <select
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="mx-2 form-select w-25"
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
              <option value={500}>500</option>
            </select>
            entries
          </div>
        </div>

        <div className="col-md-6">
          <div className="float-end">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={handleSearch}
              className="form-control"
            />
          </div>
        </div>
      </div>

      {/* Domain Table */}
      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              <th>Domain</th>
              <th>Country</th>
              <th>Register Price</th>
              <th>Registration Terms</th>
            </tr>
          </thead>

          <tbody>
            {paginated.map((d, index) => {
              const ext = normalizeExt(d.extension);

              // 🔴 CRITICAL FIX:
              // IDN gTLD pages must be grouped by maincat, not country name
              const groupName =
                d.maincat === "IDN gTLD" ? d.maincat : d.name;

              const countrySlug = slugifyCountry(groupName);
              const domainLink = `/country-domains/${countrySlug}-.${ext}`;

              const { price, term } = getPriceAndTerm(d.pricing);

              return (
                <tr key={`${countrySlug}-${ext}-${index}`}>
                  <td>
                    <Link
                      href={domainLink}
                      className="textHad fw-bold text-decoration-none"
                    >
                      .{ext}
                    </Link>
                  </td>

                  <td>
                    <Image
                      src={DomFlag}
                      width={26}
                      height={17}
                      alt="flag"
                      className="me-2"
                    />
                    {d.name}
                  </td>

                  <td>
                    {process.env.NEXT_PUBLIC_CURRENCY_SYMBOL}
                    {price}
                  </td>

                  <td>{term}</td>
                </tr>
              );
            })}

            {!paginated.length && (
              <tr>
                <td colSpan={4} className="text-center text-muted">
                  No domains found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="d-flex justify-content-between mt-3">
        <div>
          Showing {paginated.length} of {filteredData.length} entries
        </div>

        <nav>
          <ul className="pagination">
            <li
              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              onClick={() => currentPage > 1 && setCurrentPage((p) => p - 1)}
            >
              <span className="page-link">Prev</span>
            </li>

            {[...Array(totalPages)].map((_, i) => (
              <li
                key={i}
                className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                onClick={() => setCurrentPage(i + 1)}
              >
                <span className="page-link">{i + 1}</span>
              </li>
            ))}

            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
              onClick={() =>
                currentPage < totalPages &&
                setCurrentPage((p) => p + 1)
              }
            >
              <span className="page-link">Next</span>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default DomainDataTable;
